/**
 *
 * User Roles and Abilities defined in Confluence:
 * https://confluence.intra.documill.com/display/UD/User+Roles+and+Abilities
 *
 * CASL JavaScript library is used for role-based access control
 * Documentation: https://stalniy.github.io/casl/
 *
 * Abilities are applied depending on the role of the user
 * and can be used in each component.
 *
 * How to use:
 * in template: v-if="$can('access', 'routeName');
 * in script: this.$can('delete', 'project')
 *
 * NOTE: Beware of using the word 'manage' to define actions.
 *       The CASL library uses it as an alias for all actions.
 *
 * For unlimited user with all rights - can('manage', 'all')
 *
 * To display in console user abilities console.log(this.$ability.rules);
 */


import {
  AbilityBuilder,
  Ability
} from '@casl/ability';

import Collaborators from '../utils/collaborators';

export const ability = new Ability();

export function defineRulesFor(userRole) {
  const {
    can,
    cannot,
    rules
  } = new AbilityBuilder();

  switch (userRole) {
    /**
     * Organization level rights
     *
     * Action words to define permissions:
     * 'access' - access to router paths at the organization level.
     *            navigation bar Home/Projects/Workflows/Users/Organization.
     * 'create' - permission to create a project or user.
     * 'see' - used primarily for ability to see Home dashboard boxes.
     * 'edit' - ability to edit user, organization role and workflow template.
     * 'deactivate' - ability to deactivate a user account.
     * 'assign' - ability to assign organization/user groups.
     *
     * Project level rights for Organization Admins/Group Managers for projects
     * where they are non-collaborators:
     * 'view' - ability to see project related components.
     * 'copy' - permission to copy/save a project as workflow (dropdown options).
     */

    case 'ORGANIZATION_ADMIN':
      // Can access to all pages and create project or workflow.
      // Also can see all Home page boxes
      can('manage', 'all');
      can('deactivate', 'account');
      cannot('edit','externalTeam');
      break;
    case 'GROUP_MANAGER':
      can(['access', 'see', 'view', 'copy'], 'all');
      can('create', ['project', 'template']);
      can('assign', 'templateToGroup'); // Can assign only groups to which he/she belongs (available groups).
      can('edit', ['templateWorkflow']);

      // Group Managers are able to preview files in Projects they have access to. See FLOW-4303.
      can('preview', 'file');

      cannot('access', ['users', 'organization', 'customSignaturePage'])
        .because('Manager does not have access rights to Users and Organization pages');
      break;
    case 'ORGANIZATION_MEMBER':
      can('access', ['home', 'projects', 'invitation', 'oauthIntegration', 'profile', 'invitationNewUser', 'organizationUserInvitation', 'orgInvitationSignup', 'projectTask', 'templates']);
      // See FLOW-4287:
      can('copy', 'project');
      can('create', 'project');
      // This ability is defined for a display of the Home page
      // Organization member has a different view of the 'home' page
      // https://jira.intra.documill.com/browse/DOS-573
      can('see', ['projectsSummary', 'myTasks']);
      break;
    case 'GUEST':
      can('access', ['invitation', 'invitationNewUser', 'organizationUserInvitation', 'orgInvitationSignup', 'projectTask']);
      break;
    default:
      break;
  }
  return rules;
}
/**
 * Project level rights.
 *
 * We probably should not allow project roles to enable "edit"-rights
 * on templates, DOS-443.
 *
 * Action words to define permissions:
 * 'access' - access to router paths at the project level.
 *            project side bar Dashboard/Workflow/Tasks/Collaborators/Files
 * 'view' - ability to see all project tasks, rejection buttons, folder settings
 *          and collaborators box on a project dashboard.
 * 'edit' - permission to modify a project (e.g. team, workflow, collaborators etc.)
 * 'delete' - ability to delete a project.
 * 'notify' - ability to send email notifications.
 * 'upload' - define rights to upload file in project
 * 'preview' - rights to preview documents from the files folder or within tasks.
 * 'select' - rights to select a previously defined instance (e.g. a workflow).
 */
export function defineProjectRulesFor(userRole,userOrgRole) {
  const {
    can,
    cannot,
    rules
  } = new AbilityBuilder();

  switch (userRole) {
    case 'PROJECT_OWNER':
      can('view', 'all');
      can('edit', 'all');
      can('select', 'projectWorkflow');

      // Regular Users are not allowed to edit Project Workflow even if they are POs. See FLOW-4288.

      if(userOrgRole === 'ORGANIZATION_MEMBER')
        cannot('edit', 'projectWorkflow');

      cannot('edit', 'externalTeam');
      can('access', ['project', 'projectDashboard', 'projectWorkflow', 'projectTasks', 'projectTasksWithMention', 'projectTasksViewMode', 'projectCollaborators', 'projectDocuments', 'projectLog', 'quickProject', 'quickFile', 'quickEdit', 'quickReview', 'quickSign', 'quickApprove', 'quickCombine', 'quickTasks', 'quickNotifications', 'quickDeliver']);
      can(['delete', 'copy'], ['project', 'projectWorkflow']);
      can('notify','all');
      can(['upload', 'preview'], 'file');
      break;
    case 'COLLABORATOR':
      can('view', ['collaborators', 'projectKeywords', 'projectGroups']);
      can('access', ['project', 'projectDashboard', 'projectWorkflow', 'projectTasks', 'projectTasksWithMention', 'projectTasksViewMode', 'projectCollaborators', 'projectDocuments', 'quickProject', 'quickTasks']);
      cannot('view', ['folderSetting', 'allTasks', 'rejectionButtons']);
      cannot('edit', ['backgroundMaterial', 'projectRole']);
      cannot('copy', 'project');
      cannot('notify','all');
      can(['upload', 'preview'], 'file');
      break;
    case 'LIMITED_COLLABORATOR':
      can('view', ['projectKeywords', 'projectGroups']);
      can('access', ['project', 'projectDashboard', 'projectTasks', 'projectTasksWithMention', 'projectTasksViewMode', 'projectDocuments', 'quickProject', 'quickTasks']);
      cannot('access', ['projectWorkflow', 'projectCollaborators']);
      cannot('view', ['folderSetting', 'allTasks', 'rejectionButtons', 'collaborators']);
      cannot('copy', 'project');
      can(['upload', 'preview'], 'file');
      break;
    case 'TEAM_LEADER':
      can('view', ['collaborators']);
      can('access', ['project', 'projectDashboard', 'projectTasks', 'projectTasksWithMention', 'projectTasksViewMode', 'projectCollaborators', 'projectDocuments', 'projectWorkflow','projectLog']);
      can('edit', 'externalTeam');
      cannot('view', ['folderSetting', 'allTasks', 'rejectionButtons']);
      cannot('edit', ['backgroundMaterial', 'projectRole']);
      cannot('copy', 'project');
      can(['upload', 'preview'], 'file');
      break;
    default:
      cannot('view', 'folderSetting');
      break;
  }

  return rules;
}

/* Note: these rules should be kept in sync with the server-side checks that
 *       enforce the access rules
 */

export function defineAnnotationCreationRulesFor(task) {
  const {
    can,
    cannot,
    rules
  } = new AbilityBuilder();

  switch(task.userRole) {
  case Collaborators.ROLES.APPROVING_EDITOR:
  case Collaborators.ROLES.EXTERNAL_APPROVING_EDITOR:
  case Collaborators.ROLES.APPROVER:
  case Collaborators.ROLES.REVIEWER:
  case Collaborators.ROLES.EXTERNAL_REVIEWER:
  case Collaborators.ROLES.COMMENTER:
  case Collaborators.ROLES.EXTERNAL_COMMENTER:
  case Collaborators.ROLES.EDITOR:
  case Collaborators.ROLES.EXTERNAL_EDITOR:
    can("create", "Annotation");
    can("create", "AnnotationReply");
    break;

  case Collaborators.ROLES.COMBINER:
  case Collaborators.ROLES.INTERNAL_READER:
  case Collaborators.ROLES.NONE:
  case Collaborators.ROLES.OFFLINE_EDITOR:
  case Collaborators.ROLES.SIGNER:
  default:
    break;
  }

  return rules;
}

export function defineAnnotationRulesFor(store, task, annotation) {
  const {
    can,
    cannot,
    rules
  } = new AbilityBuilder();

  const isOwnAnnotation = annotation.author.type == 'COLLABORATOR' &&
        annotation.author.collaboratorId == store.getters.getCollaboratorId;
  const isOwnTeamControlled = annotation.controllingTeamId == store.getters.getUserTeam.id;
  const isPublic = annotation.latestRevision.public;
  const isFullyResolved = store.getters.teams.every(team => annotation.latestRevision.resolvedBy.includes(team.id));

  switch(task.userRole) {
  case Collaborators.ROLES.APPROVING_EDITOR:
  case Collaborators.ROLES.EXTERNAL_APPROVING_EDITOR:
    if(isOwnTeamControlled || (isPublic && isFullyResolved))
      can("delete", "Annotation", { id: annotation.id });
    if(isOwnAnnotation)
      can("edit", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled)
      can("markRelevantToLatest", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled)
      can("publish", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled || isPublic)
      can("resolve", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled || isPublic)
      can("reopen", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled)
      can("deleteAll", "Annotation");
    break;

  case Collaborators.ROLES.APPROVER:
  case Collaborators.ROLES.REVIEWER:
  case Collaborators.ROLES.EXTERNAL_REVIEWER:
    if(isOwnAnnotation)
      can("edit", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled)
      can("markRelevantToLatest", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled || isPublic) {
      can("resolve", "Annotation", { id: annotation.id });
      can("reopen", "Annotation", { id: annotation.id });
    }
    if(isOwnTeamControlled) {
      can("publish", "Annotation", { id: annotation.id });
      can("deleteAll", "Annotation");
    }
    if(isOwnTeamControlled || (isPublic && isFullyResolved))
      can("delete", "Annotation", { id: annotation.id });
    break;

  case Collaborators.ROLES.COMMENTER:
  case Collaborators.ROLES.EXTERNAL_COMMENTER:
  case Collaborators.ROLES.EDITOR:
  case Collaborators.ROLES.EXTERNAL_EDITOR:
    if(isOwnAnnotation && !isPublic)
      can("delete", "Annotation", { id: annotation.id });
    if(isOwnAnnotation)
      can("edit", "Annotation", { id: annotation.id });
    if(isOwnTeamControlled)
      can("markRelevantToLatest", "Annotation", { id: annotation.id });
    break;

  case Collaborators.ROLES.COMBINER:
  case Collaborators.ROLES.INTERNAL_READER:
  case Collaborators.ROLES.NONE:
  case Collaborators.ROLES.OFFLINE_EDITOR:
  case Collaborators.ROLES.SIGNER:
  default:
    // No permissions
    break;
  }

  return rules;
}

export function defineAnnotationReplyRulesFor(store, task, annotation, reply) {
  const {
    can,
    cannot,
    rules
  } = new AbilityBuilder();

  const isOwnReply = reply.author.type == 'COLLABORATOR' &&
        reply.author.collaboratorId == store.getters.getCollaboratorId;
  const isOwnTeamControlled = reply.controllingTeamId == store.getters.getUserTeam.id;
  const isPublic = annotation.latestRevision.public;

  switch(task.userRole) {
  case Collaborators.ROLES.APPROVING_EDITOR:
  case Collaborators.ROLES.EXTERNAL_APPROVING_EDITOR:
    if(isOwnTeamControlled || isPublic)
      can("delete", "AnnotationReply", { id: reply.id });
    if(isOwnReply)
      can("edit", "AnnotationReply", { id: reply.id });
    break;

  case Collaborators.ROLES.APPROVER:
  case Collaborators.ROLES.REVIEWER:
  case Collaborators.ROLES.EXTERNAL_REVIEWER:
  case Collaborators.ROLES.COMMENTER:
  case Collaborators.ROLES.EXTERNAL_COMMENTER:
  case Collaborators.ROLES.EDITOR:
  case Collaborators.ROLES.EXTERNAL_EDITOR:
    if(isOwnReply)
      can("delete", "AnnotationReply", { id: reply.id });
    if(isOwnReply)
      can("edit", "AnnotationReply", { id: reply.id });
    break;

  case Collaborators.ROLES.COMBINER:
  case Collaborators.ROLES.INTERNAL_READER:
  case Collaborators.ROLES.NONE:
  case Collaborators.ROLES.OFFLINE_EDITOR:
  case Collaborators.ROLES.SIGNER:
  default:
    // No permissions
    break;
  }

  return rules;
}

export default ability;
